import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { library } from "@fortawesome/fontawesome-svg-core"
import { fas } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
library.add(fas)

import PlanningContext from "./components/PlanningContext"
import Styles from "./components/Styles"
import Toolbar from "./components/Toolbar"
import ProjectPalette from "./components/ProjectPalette"
import PeoplePalette from "./components/PeoplePalette"
import PeopleGrid from "./components/PeopleGrid"
import ProjectGrid from "./components/ProjectGrid"
import CreatePopover from "./components/CreatePopover"
import EditPopover from "./components/EditPopover"
import AddProjectPopover from "./components/AddProjectPopover"
import CopyWeekModal from "./components/CopyWeekModal"
import SelectionBar from "./components/SelectionBar"
import ShortcutsHint from "./components/ShortcutsHint"
import GuideModal from "./components/GuideModal"
import useTips from "./hooks/useTips"
import Toasts from "./components/Toasts"
import useBoard from "./hooks/useBoard"
import useToasts from "./hooks/useToasts"
import useCellIndex from "./hooks/useCellIndex"
import usePlanningHandlers from "./hooks/usePlanningHandlers"
import { addDays, mondayOf, todayISO } from "./lib/dates"
import { sumHours } from "./lib/hours"
import { matches } from "./lib/search"

const SPANS          = [7, 14, 28]
const FIRST_COLUMN   = 232
const LAST_COLUMN    = 104
const COLUMN_WIDTH   = { 7: 148, 14: 98, 28: 58 }
const DENSITY        = { 7: "comfortable", 14: "regular", 28: "compact" }
const PANEL_HEIGHT   = "h-[calc(100vh-205px)] min-h-[440px]"

const readPreference = (key, fallback) => {
  try {
    const value = window.localStorage.getItem(`project_planning.${key}`)
    return value == null ? fallback : JSON.parse(value)
  } catch (_error) {
    return fallback
  }
}

const writePreference = (key, value) => {
  try { window.localStorage.setItem(`project_planning.${key}`, JSON.stringify(value)) } catch (_error) { /* private mode */ }
}

const personKey  = item => item.user_id || "none"
const projectKey = item => item.project_id

const App = ({ board: initialBoard, json_url, assignments_url, duplicate_url, copy_week_url, destroy_many_url, hours_url, project_id }) => {
  const { toasts, notify, dismiss } = useToasts()
  const urls = useMemo(() => ({
    json: json_url, assignments: assignments_url, duplicate: duplicate_url, copyWeek: copy_week_url, destroyMany: destroy_many_url,
  }), [json_url, assignments_url, duplicate_url, copy_week_url, destroy_many_url])
  const { board, loading, load, actions } = useBoard({ initial: initialBoard, urls, notify })

  const [highlightProjectId, setHighlightProjectId] = useState(project_id ? Number(project_id) : null)
  const toggleHighlight = useCallback(id => setHighlightProjectId(current => (current === id ? null : id)), [])
  const canWrite = Boolean(board.can?.write)

  const [span, setSpan]           = useState(() => (SPANS.includes(readPreference("span")) ? readPreference("span") : 14))
  const [view, setView]           = useState(() => (highlightProjectId || readPreference("view") === "projects" ? "projects" : "people"))
  const [search, setSearch]       = useState("")
  const [team, setTeam]           = useState("")
  // Site-only workers first: they are who the site planning is for.
  const [crew, setCrew]           = useState(() => (initialBoard.users.some(user => user.site_only) ? "site_only" : "all"))
  const [collapsed, setCollapsed] = useState(() => new Set(readPreference("collapsed", [])))
  const [selection, setSelection] = useState(() => new Set())
  const [range, setRange]         = useState(null)
  const [popover, setPopover]     = useState(null)
  const [copyOpen, setCopyOpen]   = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [idleOnly, setIdleOnly]   = useState(false)
  const { observeCreate } = useTips({ notify, canWrite })
  const [extraProjectIds, setExtraProjectIds] = useState([])
  const [onlyHighlighted, setOnlyHighlighted] = useState(Boolean(highlightProjectId))
  const rootRef = useRef(null)

  // The server sends two weeks; another remembered span is loaded at once.
  useEffect(() => {
    if (board.days.length !== span) load(board.from, addDays(board.from, span - 1))
  }, [])

  useEffect(() => { setSelection(new Set()) }, [board.from, board.to])

  const goTo = useCallback((start, nextSpan = span) => load(start, addDays(start, nextSpan - 1)), [load, span])

  const usersById    = useMemo(() => new Map(board.users.map(user => [user.id, user])), [board.users])
  const projectsById = useMemo(() => new Map(board.projects.map(project => [project.id, project])), [board.projects])
  const absences     = useMemo(() => {
    const map = new Map()
    board.absences.forEach(absence => {
      const key = `${absence.user_id}|${absence.date}`
      if (!map.get(key)?.all_day) map.set(key, absence)
    })
    return map
  }, [board.absences])
  const holidays = useMemo(() => new Set(board.days.filter(day => day.holiday).map(day => day.date)), [board.days])

  const inPeriod = useMemo(() => {
    const first = board.days[0]?.date
    const last  = board.days[board.days.length - 1]?.date
    return board.assignments.filter(item => item.date >= first && item.date <= last)
  }, [board.assignments, board.days])

  const personCells  = useCellIndex(board.assignments, personKey)
  const projectCells = useCellIndex(board.assignments, projectKey)

  const busyIds = useMemo(() => new Set(inPeriod.map(item => item.user_id)), [inPeriod])
  const crewUsers = useMemo(() => board.users.filter(user =>
    (crew === "all" || user.site_only) &&
    (team === "" || String(user.group_id ?? "none") === team) && matches(search, user.name)), [board.users, crew, team, search])
  const visibleUsers = useMemo(() => (idleOnly ? crewUsers.filter(user => !busyIds.has(user.id)) : crewUsers), [crewUsers, idleOnly, busyIds])
  const idleCount = useMemo(() => crewUsers.filter(user => !busyIds.has(user.id)).length, [crewUsers, busyIds])
  const crewCounts = useMemo(() => ({
    site_only: board.users.filter(user => user.site_only).length,
    all:       board.users.length,
  }), [board.users])

  const hoursByProject = useMemo(() => {
    const map = new Map()
    inPeriod.forEach(item => map.set(item.project_id, (map.get(item.project_id) || 0) + (item.hours || 0)))
    return map
  }, [inPeriod])

  const hoursByUser = useMemo(() => {
    const map = new Map()
    inPeriod.forEach(item => { if (item.user_id) map.set(item.user_id, (map.get(item.user_id) || 0) + (item.hours || 0)) })
    return map
  }, [inPeriod])

  const stats = useMemo(() => {
    const perDay = new Map()
    inPeriod.forEach(item => {
      const key = `${item.user_id}|${item.date}`
      perDay.set(key, (perDay.get(key) || 0) + (item.hours || 0))
    })
    const max = board.settings.daily_max
    return {
      hours:     sumHours(inPeriod),
      people:    new Set(inPeriod.map(item => item.user_id)).size,
      overloads: max > 0 ? [...perDay.values()].filter(hours => hours > max).length : 0,
    }
  }, [inPeriod, board.settings.daily_max])

  const projectRows = useMemo(() => {
    if (highlightProjectId && onlyHighlighted) return [projectsById.get(highlightProjectId)].filter(Boolean)
    const ids = new Set([...inPeriod.map(item => item.project_id), ...extraProjectIds])
    if (highlightProjectId) ids.add(highlightProjectId)
    return [...ids].map(id => projectsById.get(id)).filter(Boolean)
      .filter(project => matches(search, project.code, project.name, project.client))
      .sort((a, b) => (a.code || "").localeCompare(b.code || "") || a.name.localeCompare(b.name))
  }, [inPeriod, extraProjectIds, projectsById, highlightProjectId, onlyHighlighted, search])

  const isDayOff = useCallback((userId, date) =>
    holidays.has(date) || Boolean(userId && absences.get(`${userId}|${date}`)?.all_day), [holidays, absences])

  const closePopover = useCallback(() => { setPopover(null); setRange(null) }, [])

  const { handlers, dragRef } = usePlanningHandlers({
    rootRef, board, actions, notify, selection, setSelection, setPopover, setRange,
    absences, usersById, visibleUsers, isDayOff, canWrite, onCreate: observeCreate,
  })

  const context = useMemo(() => ({
    handlers, dragRef, canWrite, usersById, projectsById, highlightProjectId,
    settings: board.settings,
    density:  DENSITY[board.days.length] || (board.days.length > 14 ? "compact" : "regular"),
  }), [handlers, dragRef, canWrite, usersById, projectsById, highlightProjectId, board.settings, board.days.length])

  const template = `${FIRST_COLUMN}px repeat(${board.days.length}, minmax(${COLUMN_WIDTH[board.days.length] || 58}px, 1fr)) ${LAST_COLUMN}px`
  const weeks    = board.days.length / 7
  // The fr tracks share the width left; they never grow to fit a long title.
  const gridMinWidth = FIRST_COLUMN + LAST_COLUMN + board.days.length * (COLUMN_WIDTH[board.days.length] || 58)

  const toggleGroup = useCallback(key => setCollapsed(current => {
    const next = new Set(current)
    next.has(key) ? next.delete(key) : next.add(key)
    writePreference("collapsed", [...next])
    return next
  }), [])

  const changeSpan = value => { setSpan(value); writePreference("span", value); goTo(board.from, value) }
  const changeView = value => { setView(value); writePreference("view", value); setSelection(new Set()); closePopover() }

  const selectedIds = [...selection]
  const editing = popover?.kind === "edit" ? board.assignments.find(item => item.id === popover.id) : null
  const highlighted = highlightProjectId ? projectsById.get(highlightProjectId) : null

  return (
    <PlanningContext.Provider value={context}>
      <Styles />
      <div ref={rootRef} className="relative font-sans">
        {loading && (
          <div className="absolute top-0 inset-x-0 h-0.5 overflow-hidden bg-brand-100">
            <div className="pp-loading-bar h-full w-1/3 bg-brand-500" />
          </div>
        )}

        <Toolbar
          from={board.from}
          to={board.to}
          span={span}
          view={view}
          search={search}
          team={team}
          crew={crew}
          crewCounts={crewCounts}
          onCrew={setCrew}
          idleOnly={idleOnly}
          idleCount={idleCount}
          onIdle={() => setIdleOnly(value => !value)}
          onGuide={() => setGuideOpen(true)}
          groups={board.groups}
          stats={stats}
          canWrite={canWrite}
          hoursUrl={hours_url}
          loading={loading}
          onShift={direction => goTo(addDays(board.from, direction * 7))}
          onToday={() => goTo(mondayOf(todayISO()))}
          onSpan={changeSpan}
          onView={changeView}
          onSearch={setSearch}
          onTeam={setTeam}
          onCopyWeek={() => setCopyOpen(true)}
        />

        <div className="flex items-start gap-4 px-6 pb-6">
          <aside className={`w-[264px] shrink-0 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden ${PANEL_HEIGHT}`}>
            {view === "people"
              ? <ProjectPalette projects={board.projects} hoursByProject={hoursByProject} highlightProjectId={highlightProjectId} onToggle={toggleHighlight} />
              : <PeoplePalette users={board.users} groups={board.groups} hoursByUser={hoursByUser} weeks={weeks} />}
            {canWrite && <ShortcutsHint view={view} onGuide={() => setGuideOpen(true)} />}
          </aside>

          <section className="flex-1 min-w-0 flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden">
            {highlighted && (
              <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-brand-50/60 text-[12.5px]">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: highlighted.color }} />
                <span className="text-slate-700">
                  {view === "projects" && onlyHighlighted
                    ? I18n.t("project_planning.projects_view.filtered_on", { project: highlighted.title })
                    : I18n.t("project_planning.projects_view.highlighted", { project: highlighted.title })}
                </span>
                {view === "projects" && (
                  <button type="button" onClick={() => setOnlyHighlighted(value => !value)} className="ml-auto font-medium text-brand-600 hover:text-brand-700">
                    {onlyHighlighted ? I18n.t("project_planning.projects_view.show_all") : I18n.t("project_planning.projects_view.show_only")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setHighlightProjectId(null); setOnlyHighlighted(false) }}
                  title={I18n.t("project_planning.projects_view.clear")}
                  className={`${view === "projects" ? "" : "ml-auto "}inline-flex items-center gap-1 font-medium text-slate-500 hover:text-slate-800`}
                >
                  <FontAwesomeIcon icon="xmark" />
                  {I18n.t("project_planning.projects_view.clear")}
                </button>
              </div>
            )}
            {canWrite && view === "people" && inPeriod.length === 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 border-b border-amber-200 bg-amber-50 text-[12.5px] text-amber-900">
                <FontAwesomeIcon icon="lightbulb" className="text-amber-500" />
                <span>{I18n.t("project_planning.guide.empty")}</span>
                <button type="button" onClick={() => setGuideOpen(true)} className="ml-auto font-semibold text-amber-800 hover:text-amber-950">
                  {I18n.t("project_planning.guide.all_tips")}
                </button>
              </div>
            )}
            <div className={`overflow-auto ${PANEL_HEIGHT}`} style={{ "--grid-min": `${gridMinWidth}px` }}>
              {view === "people" ? (
                <PeopleGrid
                  board={board}
                  users={visibleUsers}
                  cells={personCells}
                  absences={absences}
                  selection={selection}
                  range={range}
                  collapsed={collapsed}
                  onToggleGroup={toggleGroup}
                  template={template}
                  density={context.density}
                />
              ) : (
                <ProjectGrid
                  board={{ ...board, assignments: inPeriod }}
                  projects={projectRows}
                  extraIds={extraProjectIds}
                  cells={projectCells}
                  selection={selection}
                  template={template}
                  density={context.density}
                  highlightProjectId={highlightProjectId}
                  canWrite={canWrite}
                  onAddProject={rect => setPopover({ kind: "addProject", rect })}
                  onRemoveExtra={id => setExtraProjectIds(ids => ids.filter(other => other !== id))}
                />
              )}
            </div>
          </section>
        </div>
      </div>

      {popover?.kind === "create" && (
        <CreatePopover
          key={`${popover.target.userId}-${popover.target.projectId}-${popover.target.dates.join()}`}
          rect={popover.rect}
          target={popover.target}
          board={board}
          defaultProjectId={highlightProjectId || handlers.lastProjectId()}
          isDayOff={isDayOff}
          onSubmit={handlers.createFromPopover}
          onClose={closePopover}
        />
      )}
      {popover?.kind === "edit" && editing && (
        <EditPopover
          key={editing.id}
          rect={popover.rect}
          assignment={editing}
          board={board}
          canWrite={canWrite && typeof editing.id === "number"}
          onSave={actions.update}
          onDelete={actions.remove}
          onClose={closePopover}
        />
      )}
      {popover?.kind === "addProject" && (
        <AddProjectPopover
          rect={popover.rect}
          projects={board.projects.filter(project => !projectRows.includes(project) && !["completed", "cancelled"].includes(project.status))}
          onPick={id => { setOnlyHighlighted(false); setExtraProjectIds(ids => [...ids, id]) }}
          onClose={closePopover}
        />
      )}

      {copyOpen && (
        <CopyWeekModal
          isOpen
          onClose={() => setCopyOpen(false)}
          days={board.days}
          assignments={board.assignments}
          onConfirm={actions.copyWeek}
        />
      )}

      {selectedIds.length > 0 && canWrite && (
        <SelectionBar
          count={selectedIds.length}
          onDuplicateWeek={() => { actions.duplicate(selectedIds, { dayOffset: 7 }); setSelection(new Set()) }}
          onDelete={() => { actions.remove(selectedIds); setSelection(new Set()) }}
          onClear={() => setSelection(new Set())}
        />
      )}

      {guideOpen && <GuideModal isOpen onClose={() => setGuideOpen(false)} />}

      <Toasts toasts={toasts} onDismiss={dismiss} />
    </PlanningContext.Provider>
  )
}

export default App
